import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Download, Share2, DollarSign, TrendingUp, TrendingDown, Home, X, Copy, Check,
  SlidersHorizontal, MapPin, GraduationCap, Bus, ShoppingBag, TreePine,
  UtensilsCrossed, Heart, FileText, Mail, BarChart3, Loader2, Brain, Lock, Plus, Save, AlertTriangle
} from 'lucide-react';
import { RendPlexLogo } from '../components/RendPlexLogo';
import DarkModeToggle from '../components/DarkModeToggle';
import { ResultsDisclaimer, DealScoreDisclaimer, SaveDealDisclaimer, Footer } from '../components/LegalDisclaimers';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PropertyMap from '../components/PropertyMap';
import NeighborhoodInsights from '../components/NeighborhoodInsights';
import PropertyIntelligencePanel from './PropertyIntelligencePanel';

import DecisionHero from '../components/deal/DecisionHero';
import KpiStrip from '../components/deal/KpiStrip';
import NegotiationAssistant from '../components/deal/NegotiationAssistant';
import DealRescue from '../components/deal/DealRescue';
import ScenarioCards from '../components/deal/ScenarioCards';
import ScoreImprovement from '../components/deal/ScoreImprovement';
import InvestmentTimeline from '../components/deal/InvestmentTimeline';
import AiInsights from '../components/deal/AiInsights';
import ActionPlan from '../components/deal/ActionPlan';
import CollapsibleSection from '../components/deal/CollapsibleSection';
import AIPropertyChat from '../components/AIPropertyChat';
import InvestorFitCard from '../components/deal/InvestorFitCard';
import ProfileNudgeBanner from '../components/deal/ProfileNudgeBanner';
import { PropertyAdvisorProvider } from '../context/PropertyAdvisorContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DealView() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [deal, setDeal] = useState(null);
  const [user, setUser] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [streetViewUrl, setStreetViewUrl] = useState(null);
  const [loadingSV, setLoadingSV] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [savingDeal, setSavingDeal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [neighborhoodData, setNeighborhoodData] = useState(null);
  const [investorProfile, setInvestorProfile] = useState(null);
  const dealCardRef = useRef(null);
  const aiSectionRef = useRef(null);

  // Wave 1B.2A — feature-access gates use `effective_capabilities`.
  const effCaps = user?.effective_capabilities || [];
  const isPremium = effCaps.includes('portfolio_ai') || user?.subscription_plan === 'premium';
  const isPro = isPremium || effCaps.includes('ai_advisor') || user?.subscription_plan === 'pro';

  // Load deal + user
  useEffect(() => {
    const loadDeal = async () => {
      try {
        const [dealResp, userResp] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/deals/${dealId}`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }).catch(() => null),
        ]);
        setDeal(dealResp.data);
        if (userResp?.data) setUser(userResp.data);

        if (dealResp.data.property_lat && dealResp.data.property_lng) {
          try {
            const nbResp = await axios.post(`${BACKEND_URL}/api/property/neighborhood`, {
              latitude: dealResp.data.property_lat, longitude: dealResp.data.property_lng, radius: 1000,
            }, { withCredentials: true });
            setNeighborhoodData(nbResp.data);
          } catch (e) { /* neighborhood optional */ }
        }

        // Load investor profile so the AI Advisor can personalize its answers.
        try {
          const pResp = await axios.get(`${BACKEND_URL}/api/investor-profile`, { withCredentials: true });
          if (pResp.data?.is_complete) setInvestorProfile(pResp.data);
        } catch { /* profile optional */ }
      } catch (error) {
        console.error('Failed to load deal:', error);
        toast.error(t('errors.loadFailed'));
        navigate('/dashboard');
      } finally { setLoading(false); }
    };
    loadDeal();
  }, [dealId, navigate, t]);

  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  // Load AI data for Premium — re-fetch when language changes
  useEffect(() => {
    if (!deal || !isPremium) return;
    setAiLoading(true);
    setAiData(null);
    axios.post(`${BACKEND_URL}/api/deals/${deal.deal_id}/ai-analysis`, { language: currentLang }, { withCredentials: true })
      .then(r => setAiData(r.data))
      .catch(err => console.error('AI analysis failed:', err))
      .finally(() => setAiLoading(false));
  }, [deal?.deal_id, isPremium, currentLang]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Utility functions ---
  const generateDealCard = async () => {
    if (!dealCardRef.current) return;
    try {
      setGeneratingImage(true);
      const dataUrl = await toPng(dealCardRef.current, { quality: 0.95, pixelRatio: 2 });
      const link = document.createElement('a');
      link.download = `deal-card-${deal.property_address.replace(/[^a-z0-9]/gi, '-')}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(t('deal.dealCardDownloaded'));
    } catch (error) { toast.error(t('deal.dealCardFailed')); }
    finally { setGeneratingImage(false); }
  };

  const downloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await axios.get(`${BACKEND_URL}/api/deals/${dealId}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `RendPleX_${deal.property_address.replace(/[^a-z0-9]/gi, '-').substring(0, 50)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('deal.pdfDownloaded'));
    } catch (error) { toast.error(t('deal.pdfFailed')); }
    finally { setDownloadingPdf(false); }
  };

  const saveDeal = async () => {
    try {
      setSavingDeal(true);
      await axios.post(`${BACKEND_URL}/api/deals/${dealId}/save`, {}, { withCredentials: true });
      setDeal({ ...deal, is_saved: true });
      toast.success(t('deal.dealSaved'));
    } catch (error) { toast.error(t('deal.saveFailed')); }
    finally { setSavingDeal(false); }
  };

  const removeFromPortfolio = async () => {
    if (!window.confirm(t('myPortfolio.actions.confirmRemove'))) return;
    try {
      setSavingDeal(true);
      await axios.post(`${BACKEND_URL}/api/deals/${dealId}/unsave`, {}, { withCredentials: true });
      setDeal({ ...deal, is_saved: false });
      toast.success(t('myPortfolio.actions.removed'));
    } catch { toast.error(t('deal.saveFailed')); }
    finally { setSavingDeal(false); }
  };

  const runScenario = () => navigate(`/scenario?deal_id=${dealId}`);
  const runRentOptimizer = () => navigate(`/rent-optimizer/${dealId}`);

  const emailReport = async () => {
    try {
      setSendingEmail(true);
      await axios.post(`${BACKEND_URL}/api/deals/${dealId}/email`, {}, { withCredentials: true });
      toast.success(t('deal.emailSent'));
    } catch (error) { toast.error(error.response?.data?.detail || t('deal.emailFailed')); }
    finally { setSendingEmail(false); }
  };

  const getShareUrl = () => window.location.href;
  const getShareText = () => {
    const cf = deal.monthly_cash_flow > 0 ? `+$${deal.monthly_cash_flow.toLocaleString()}` : `-$${Math.abs(deal.monthly_cash_flow).toLocaleString()}`;
    return t('share.shareText', { score: deal.deal_score, cashFlow: cf, roi: deal.roi.toFixed(1) });
  };

  const shareNative = async () => {
    if (navigator.share) {
      const payload = { title: `${t('deal.title')}: ${deal.property_address}`, text: getShareText(), url: getShareUrl() };
      if (dealCardRef.current && navigator.canShare) {
        try {
          const dataUrl = await toPng(dealCardRef.current, { quality: 0.95, pixelRatio: 2 });
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], 'rendplex-deal-card.png', { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) payload.files = [file];
        } catch (e) { /* image attach optional */ }
      }
      try {
        await navigator.share(payload);
      } catch (e) { if (e.name !== 'AbortError') setShowShareModal(true); }
    } else { setShowShareModal(true); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      toast.success(t('share.linkCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error(t('share.copyFailed')); }
  };

  const scrollToAi = () => {
    aiSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          <p className="mt-4 text-slate-600 dark:text-slate-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }
  if (!deal) return null;

  // Build the property snapshot the RendPleX AI Advisor should see for THIS deal.
  // aiData (from the AI Decision Engine) may not have loaded yet — those fields
  // will simply be absent from the context until it arrives.
  const advisorContext = {
    address: deal.property_address,
    purchase_price: deal.purchase_price,
    num_units: deal.num_units,
    annual_income: deal.annual_income,
    monthly_income: deal.monthly_income,
    down_payment_percent: deal.down_payment_percent,
    interest_rate: deal.interest_rate,
    loan_term_years: deal.loan_term_years,
    annual_property_tax: deal.annual_property_tax,
    annual_insurance: deal.annual_insurance,
    monthly_maintenance: deal.monthly_maintenance,
    vacancy_rate: deal.vacancy_rate,
    property_management_percent: deal.property_management_percent,
    monthly_mortgage: deal.monthly_mortgage,
    monthly_expenses: deal.monthly_expenses,
    monthly_cash_flow: deal.monthly_cash_flow,
    annual_cash_flow: deal.annual_cash_flow,
    noi: deal.noi,
    cap_rate: deal.cap_rate,
    cash_on_cash: deal.cash_on_cash,
    roi: deal.roi,
    deal_score: deal.deal_score,
    investment_rating: aiData?.decision?.recommendation || aiData?.decision?.band,
    suggested_offer_price: deal.suggested_offer_price,
    risk_factors: aiData?.risks || aiData?.risk_factors,
    scenarios: aiData?.scenarios,
    investor_profile: investorProfile,
    // Routing metadata so Advisor feature chips can deep-link tools to THIS deal.
    deal_id: dealId,
  };

  return (
    <PropertyAdvisorProvider value={advisorContext}>
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 backdrop-saturate-150 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-3">
          <div className="flex justify-between items-center">
            <Link to="/dashboard" className="flex items-center gap-2"><RendPlexLogo size="md" /></Link>
            <div className="flex items-center gap-2">
              <DarkModeToggle />
              <LanguageSwitcher variant="minimal" />
              <Button data-testid="back-to-dashboard-button" onClick={() => navigate('/dashboard')} variant="ghost" size="sm"
                className="text-slate-500 dark:text-slate-400 hover:text-[#12355B] dark:hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('nav.backToDashboard')}
              </Button>
              <Button data-testid="new-analysis-button" onClick={() => navigate('/analyze')} size="sm"
                className="bg-[#12355B] hover:bg-[#1565C0] text-white dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#0F172A] font-semibold">
                <Plus className="w-4 h-4 mr-1.5" /> {t('dashboard.newAnalysis')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        {/* First-visit nudge to complete Investor Profile */}
        <ProfileNudgeBanner userId={user?.user_id || user?.email} />
        {/* Unsaved notice */}
        {deal.is_saved === false && (
          <div data-testid="unsaved-banner" className="flex items-center gap-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 rounded-xl px-4 py-3 mb-6">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-300 flex-1">{t('deal.unsavedNotice')}</p>
            <button data-testid="unsaved-banner-save-link" onClick={saveDeal} disabled={savingDeal}
              className="text-sm font-semibold text-[#12355B] dark:text-[#45C04A] hover:underline whitespace-nowrap">
              {savingDeal ? t('deal.savingDeal') : t('deal.saveDeal')}
            </button>
          </div>
        )}
        {/* Property Header */}
        <div className="mb-6">
          {/* Action buttons — placed above the property address, right-aligned */}
          <div className="flex items-center justify-end gap-2 flex-wrap mb-3">
            {deal.is_saved === false ? (
              <Button data-testid="add-to-portfolio-button" onClick={saveDeal} disabled={savingDeal} size="sm"
                className="bg-[#45C04A] hover:bg-[#65D95A] text-white dark:text-[#12355B] rounded-xl font-semibold">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {savingDeal ? t('deal.savingDeal') : t('dealView.addToPortfolio')}
              </Button>
            ) : (
              <>
                <span data-testid="in-portfolio-badge" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
                  <Check className="w-3 h-3" />{t('dealView.inPortfolio')}
                </span>
                <Button data-testid="remove-from-portfolio-button" onClick={removeFromPortfolio} disabled={savingDeal} size="sm" variant="ghost"
                  className="text-slate-400 hover:text-red-500 rounded-xl">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </>
            )}
            <Button data-testid="run-scenario-button" onClick={runScenario} size="sm" variant="outline" className="rounded-xl">
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" /> {t('myPortfolio.actions.runScenario')}
            </Button>
            <Button data-testid="rent-optimizer-button" onClick={runRentOptimizer} size="sm" className="rounded-xl bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B]">
              <DollarSign className="w-3.5 h-3.5 mr-1.5" /> {t('deal.rentOptimizerCta')}
            </Button>
            <Button data-testid="download-pdf-button" onClick={downloadPdf} disabled={downloadingPdf} size="sm"
              className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> {downloadingPdf ? t('deal.generatingPdf') : t('deal.downloadPdf')}
            </Button>
            <Button data-testid="email-report-button" onClick={emailReport} disabled={sendingEmail} size="sm" variant="outline" className="rounded-xl">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> {sendingEmail ? t('deal.sendingEmail') : t('deal.emailReport')}
            </Button>
            <Button data-testid="share-deal-card-button" onClick={shareNative} size="sm" variant="outline" className="rounded-xl">
              <Share2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          {/* Full property address (never truncated) */}
          <h1 data-testid="deal-address" className="font-primary font-bold text-2xl md:text-3xl text-slate-900 dark:text-white break-words">{deal.property_address}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('deal.analysisCreated')} {new Date(deal.created_at).toLocaleDateString()}</p>
        </div>

        {/* Map + Street View - compact row */}
        {(deal.property_lat || deal.property_image_url) && (
          <div className="flex gap-3 mb-6 overflow-hidden rounded-xl">
            {streetViewUrl || deal.property_image_url ? (
              <div className="relative w-full md:w-1/2 h-[180px] rounded-xl overflow-hidden">
                <img src={streetViewUrl || deal.property_image_url} alt={deal.property_address}
                  className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                <div
                  data-testid="deal-street-view-address-label"
                  className="absolute top-2 left-2 right-2 flex items-start gap-1.5 bg-white/95 dark:bg-[#0B0E14]/95 text-slate-800 dark:text-slate-100 text-[11px] px-2 py-1 rounded shadow-sm pointer-events-none"
                >
                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#45C04A]" />
                  <span className="font-semibold break-words leading-tight">{deal.property_address}</span>
                </div>
                <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded">
                  <MapPin className="w-2.5 h-2.5" /> {t('analyze.streetViewAvailable')}
                </div>
              </div>
            ) : null}
            {deal.property_lat && deal.property_lng && (
              <div className={`${streetViewUrl || deal.property_image_url ? 'w-1/2 hidden md:block' : 'w-full'} h-[180px]`}>
                <PropertyMap latitude={deal.property_lat} longitude={deal.property_lng} address={deal.property_address} height="180px" className="rounded-xl" />
              </div>
            )}
          </div>
        )}
        {deal.property_lat && !streetViewUrl && !deal.property_image_url && !loadingSV && (
          <button data-testid="load-streetview-deal-btn" onClick={async () => {
            setLoadingSV(true);
            try { const r = await axios.post(`${BACKEND_URL}/api/property/streetview`, { address: deal.property_address }); if (r.data?.image_url) setStreetViewUrl(r.data.image_url); }
            catch { /* SV unavailable */ } setLoadingSV(false);
          }} className="mb-6 text-sm font-medium text-[#12355B] dark:text-[#45C04A] bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            {t('analyze.loadStreetView')}
          </button>
        )}
        {loadingSV && (
          <div className="flex items-center gap-2 mb-6 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('analyze.loadingImage')}
          </div>
        )}

        {/* ============= MAIN CONTENT ============= */}
        <div className="space-y-4">

          {/* --- PREMIUM: AI Investment Decision Dashboard --- */}
          {isPremium && (
            <>
              {aiLoading && !aiData && <AiLoadingState t={t} />}
              {aiData && (
                <>
                  <DecisionHero aiData={aiData} deal={deal} onExpandAnalysis={scrollToAi} />
                  <KpiStrip deal={deal} />
                  <LivePreviewSection deal={deal} t={t} />

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <NegotiationAssistant deal={deal} />
                    <DealRescue deal={deal} />
                  </div>

                  <ScenarioCards scenarios={aiData.scenarios} />

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ScoreImprovement deal={deal} />
                    <InvestmentTimeline deal={deal} />
                  </div>

                  <div ref={aiSectionRef}>
                    <AiInsights aiData={aiData} />
                  </div>

                  <ActionPlan />
                </>
              )}

              {/* Collapsible Property Details */}
              <CollapsibleSection title={t('deal.propertyDetails')} icon={Home} testId="section-property-details">
                <PropertyDetailsGrid deal={deal} t={t} />
              </CollapsibleSection>

              <CollapsibleSection title={t('deal.cashFlowBreakdown')} icon={DollarSign} testId="section-cashflow">
                <CashFlowContent deal={deal} t={t} />
              </CollapsibleSection>

              <CollapsibleSection title={t('deal.expensesDetail')} icon={BarChart3} testId="section-expenses">
                <ExpensesContent deal={deal} t={t} />
              </CollapsibleSection>
            </>
          )}

          {/* --- PRO/FREE: Classic Layout --- */}
          {!isPremium && (
            <>
              {/* Deal Score Card */}
              <DealScoreCard deal={deal} t={t} />
              <KpiStrip deal={deal} />
              <LivePreviewSection deal={deal} t={t} />

              {/* Property Details */}
              <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
                <h3 className="font-primary font-bold text-xl text-slate-900 dark:text-white mb-6">{t('deal.propertyDetails')}</h3>
                <PropertyDetailsGrid deal={deal} t={t} />
              </div>

              {/* Cash Flow Breakdown */}
              <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
                <h3 className="font-primary font-bold text-xl text-slate-900 dark:text-white mb-6">{t('deal.cashFlowBreakdown')}</h3>
                <CashFlowContent deal={deal} t={t} />
                <div className="mt-6 pt-5 border-t border-slate-200 dark:border-slate-800 space-y-3">
                  <Button data-testid="scenario-analyzer-button" onClick={() => navigate('/scenario', { state: {
                    purchasePrice: deal.purchase_price, interestRate: deal.interest_rate,
                    annualRent: deal.annual_income, downPaymentPercent: deal.down_payment_percent,
                  }})} className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-2xl py-3">
                    <SlidersHorizontal className="w-4 h-4 mr-2" /> {t('deal.openScenarioAnalyzer')}
                  </Button>
                  <Button data-testid="compare-properties-button" onClick={() => navigate('/dashboard', { state: { openCompare: true } })}
                    className="w-full bg-[#1565C0] hover:bg-[#12355B] dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-2xl py-3">
                    <BarChart3 className="w-4 h-4 mr-2" /> {t('deal.compareProperties')}
                  </Button>
                </div>
              </div>

              {/* Expenses */}
              <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
                <h3 className="font-primary font-bold text-xl text-slate-900 dark:text-white mb-6">{t('deal.expensesDetail')}</h3>
                <ExpensesContent deal={deal} t={t} />
              </div>

              {/* Suggested Offer */}
              <div className="bg-gradient-to-br from-[#45C04A]/5 to-[#45C04A]/10 dark:from-[#45C04A]/10 dark:to-[#45C04A]/5 border-2 border-[#45C04A]/20 rounded-2xl p-6">
                <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white mb-2">{t('deal.suggestedOfferPrice')}</h3>
                <p className="text-4xl font-primary font-black text-[#45C04A] mb-2">${deal.suggested_offer_price.toLocaleString()}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                  {deal.suggested_offer_price < deal.purchase_price
                    ? t('deal.negotiateBelow', { percent: (((deal.purchase_price - deal.suggested_offer_price) / deal.purchase_price) * 100).toFixed(1) })
                    : t('deal.pricedRight')}
                </p>
                <SaveDealDisclaimer />
              </div>

              {/* Premium Upgrade CTA */}
              <PremiumUpgrade t={t} />
            </>
          )}

          {/* --- SHARED SECTIONS --- */}
          {/* Personalized Investor Fit (personalization layer, always visible) */}
          <InvestorFitCard dealId={deal.deal_id} />

          {/* Property Intelligence (Pro+) */}
          {deal.property_address && (
            <PropertyIntelligencePanel address={deal.property_address} dealId={deal.deal_id} isPro={isPro} />
          )}

          {/* Neighborhood fallback (Free only) */}
          {deal.property_lat && deal.property_lng && !isPro && (
            <NeighborhoodInsights latitude={deal.property_lat} longitude={deal.property_lng} />
          )}

          {/* Deal Card Preview */}
          <DealCardSection deal={deal} dealCardRef={dealCardRef} neighborhoodData={neighborhoodData} aiData={aiData}
            shareNative={shareNative} generateDealCard={generateDealCard} generatingImage={generatingImage} t={t} />

          <ResultsDisclaimer />
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal deal={deal} t={t} getShareUrl={getShareUrl} getShareText={getShareText}
          copyLink={copyLink} copied={copied} generateDealCard={generateDealCard} generatingImage={generatingImage}
          onClose={() => setShowShareModal(false)} />
      )}

      <Footer />

      {/* Floating RendPleX AI Advisor — auto-consumes deal context */}
      <AIPropertyChat />
    </div>
    </PropertyAdvisorProvider>
  );
}

/* ========== Extracted Sub-sections ========== */

function AiLoadingState({ t }) {
  return (
    <div data-testid="ai-analysis-loading" className="bg-gradient-to-br from-[#0a0a1a] to-[#12121f] rounded-2xl p-10 text-center border border-white/[0.06]">
      <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse">
        <Brain className="w-6 h-6 text-white" />
      </div>
      <div className="flex items-center justify-center gap-2 mb-2">
        <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
        <span className="text-sm text-slate-400">{t('decision.aiAnalyzing')}</span>
      </div>
      <p className="text-xs text-slate-500">{t('decision.aiAnalyzingDesc')}</p>
    </div>
  );
}

function LivePreviewSection({ deal, t }) {
  const monthlyCF = deal.monthly_cash_flow;
  const isProfitable = monthlyCF > 0;
  const isHighlyProfitable = monthlyCF > 500;
  const downPayment = deal.purchase_price * (deal.down_payment_percent / 100);
  const noi = deal.noi ?? (deal.annual_income - deal.monthly_expenses * 12);
  const cashOnCash = deal.cash_on_cash ?? (downPayment > 0 ? (deal.annual_cash_flow / downPayment) * 100 : 0);
  const rows = [
    { label: t('analyze.monthlyIncome'), value: `$${deal.monthly_income.toFixed(2)}` },
    { label: t('analyze.monthlyMortgage'), value: `-$${deal.monthly_mortgage.toFixed(2)}` },
    { label: t('analyze.monthlyExpenses'), value: `-$${deal.monthly_expenses.toFixed(2)}` },
    { label: t('analyze.annualCashFlow'), value: `${deal.annual_cash_flow >= 0 ? '+' : ''}$${deal.annual_cash_flow.toFixed(2)}`, cls: deal.annual_cash_flow >= 0 ? 'text-green-600' : 'text-red-600' },
    { label: t('analyze.capRate'), value: `${deal.cap_rate.toFixed(2)}%` },
    { label: t('landing.roi'), value: `${deal.roi.toFixed(2)}%` },
    { label: t('analyze.noi'), value: `$${noi.toFixed(2)}`, cls: noi < 0 ? 'text-red-600' : '' },
    { label: t('analyze.cashOnCash'), value: `${cashOnCash.toFixed(2)}%` },
    { label: t('analyze.downPayment'), value: `$${downPayment.toFixed(2)}` },
  ];
  return (
    <div data-testid="live-preview-section" className={`border-2 rounded-2xl p-6 ${
      isHighlyProfitable
        ? 'bg-green-50 dark:bg-green-500/[0.07] border-green-400'
        : isProfitable
        ? 'bg-blue-50 dark:bg-blue-500/[0.07] border-blue-400'
        : 'bg-red-50 dark:bg-red-500/[0.07] border-red-400'
    }`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">{t('analyze.livePreview')}</h3>
        {isProfitable ? (
          <TrendingUp className={`w-6 h-6 ${isHighlyProfitable ? 'text-green-600' : 'text-blue-600'}`} />
        ) : (
          <TrendingDown className="w-6 h-6 text-red-600" />
        )}
      </div>
      <div className={`rounded-2xl p-4 mb-4 ${
        isHighlyProfitable ? 'bg-green-100 dark:bg-green-500/15' : isProfitable ? 'bg-blue-100 dark:bg-blue-500/15' : 'bg-red-100 dark:bg-red-500/15'
      }`}>
        <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">{t('landing.monthlyCashFlow')}</p>
        <p className={`text-3xl font-primary font-black ${
          isHighlyProfitable ? 'text-green-700 dark:text-green-400' : isProfitable ? 'text-blue-700 dark:text-blue-400' : 'text-red-700 dark:text-red-400'
        }`}>
          {monthlyCF >= 0 ? '+' : ''}${monthlyCF.toFixed(2)}
        </p>
        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
          {isHighlyProfitable
            ? `🎉 ${t('analyze.excellentDeal')}`
            : isProfitable
            ? `✅ ${t('analyze.positiveCashFlow')}`
            : `⚠️ ${t('analyze.negativeCashFlow')}`}
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1">
        {rows.map(({ label, value, cls }) => (
          <div key={label} className="flex justify-between items-center py-2 border-b border-slate-200 dark:border-slate-700">
            <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
            <span className={`font-medium tabular-nums ${cls || 'text-[#12355B] dark:text-white'}`}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DealScoreCard({ deal, t }) {
  const { i18n } = useTranslation();
  const color = deal.deal_score >= 70 ? '#45C04A' : deal.deal_score >= 40 ? '#F59E0B' : '#EF4444';
  const lang = i18n.language?.split('-')[0] || 'en';
  const localizedExplanation =
    deal.deal_score_explanation_i18n?.[lang]
    || (lang === 'en' ? deal.deal_score_explanation : t('deal.scoreExplanationFallback'));
  return (
    <div data-testid="deal-score-card" className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 text-center">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">{t('deal.dealScore')}</p>
      <div className="relative inline-flex items-center justify-center w-32 h-32 mb-4">
        <svg className="-rotate-90 w-32 h-32" viewBox="0 0 128 128">
          <circle cx="64" cy="64" r="54" fill="none" stroke="currentColor" className="text-slate-100 dark:text-slate-800" strokeWidth="6" />
          <circle cx="64" cy="64" r="54" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
            strokeDasharray={`${(deal.deal_score / 100) * 339} 339`} />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className="text-5xl font-black" style={{ color }}>{deal.deal_score}</span>
          <span className="text-[9px] text-slate-400 font-bold tracking-widest">/100</span>
        </div>
      </div>
      <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
        deal.deal_score >= 70 ? 'bg-[#45C04A]/10 text-[#45C04A]' : deal.deal_score >= 40 ? 'bg-amber-500/10 text-amber-500' : 'bg-red-500/10 text-red-500'
      }`}>
        {deal.deal_score >= 80 ? t('deal.excellentDeal') : deal.deal_score >= 70 ? t('deal.goodDeal') : deal.deal_score >= 40 ? t('deal.fairDeal') : t('deal.poorDeal')}
      </span>
      <p data-testid="deal-score-explanation" className="text-sm text-slate-600 dark:text-slate-300 mt-3 max-w-md mx-auto">{localizedExplanation}</p>
      <DealScoreDisclaimer />
    </div>
  );
}

function PremiumUpgrade({ t }) {
  return (
    <div data-testid="premium-upgrade-cta" className="bg-gradient-to-br from-[#1a1040] to-[#2d1b69] rounded-2xl p-6 text-center border border-violet-500/20">
      <Lock className="w-8 h-8 text-violet-400 mx-auto mb-3" />
      <h3 className="font-bold text-lg text-white mb-1">{t('decision.premiumRequired')}</h3>
      <p className="text-sm text-slate-400 mb-4">{t('aiEngine.premiumRequired')}</p>
      <Button onClick={() => window.location.href = '/pricing'}
        className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#12355B] rounded-xl font-bold px-6">
        {t('decision.upgradePremium')}
      </Button>
    </div>
  );
}

function PropertyDetailsGrid({ deal, t }) {
  const items = [
    { label: t('deal.purchasePrice'), value: `$${deal.purchase_price.toLocaleString()}` },
    { label: t('deal.numberOfUnits'), value: deal.num_units },
    { label: t('deal.annualIncome'), value: `$${deal.annual_income.toLocaleString()}` },
    { label: t('deal.monthlyIncome'), value: `$${deal.monthly_income.toLocaleString()}` },
    { label: t('deal.downPaymentPercent'), value: `${deal.down_payment_percent}%` },
    { label: t('deal.interestRate'), value: `${deal.interest_rate}%` },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
      {items.map(({ label, value }) => (
        <div key={label}>
          <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
          <p className="text-lg font-medium text-slate-900 dark:text-white tabular-nums">{value}</p>
        </div>
      ))}
    </div>
  );
}

function CashFlowContent({ deal, t }) {
  const rows = [
    { label: t('deal.monthlyIncome'), value: deal.monthly_income, sign: '+', cls: 'text-emerald-600' },
    { label: t('deal.mortgagePayment'), value: deal.monthly_mortgage, sign: '-', cls: 'text-red-600' },
    { label: t('deal.operatingExpenses'), value: deal.monthly_expenses, sign: '-', cls: 'text-red-600' },
  ];
  return (
    <div className="space-y-3">
      {rows.map(({ label, value, sign, cls }) => (
        <div key={label} className="flex justify-between items-center pb-3 border-b border-slate-100 dark:border-slate-800">
          <span className="text-slate-600 dark:text-slate-400">{label}</span>
          <span className={`font-medium tabular-nums ${cls}`}>{sign}${Math.round(value).toLocaleString()}</span>
        </div>
      ))}
      <div className="flex justify-between items-center pt-2 border-t-2 border-slate-300 dark:border-slate-600">
        <span className="font-bold text-slate-900 dark:text-white">{t('deal.netCashFlow')}</span>
        <span className={`text-xl font-black tabular-nums ${deal.monthly_cash_flow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {deal.monthly_cash_flow >= 0 ? '+' : ''}${deal.monthly_cash_flow.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function ExpensesContent({ deal, t }) {
  const items = [
    { label: t('deal.propertyTaxAnnual'), value: `$${deal.annual_property_tax.toLocaleString()}` },
    { label: t('deal.insuranceAnnual'), value: `$${deal.annual_insurance.toLocaleString()}` },
    { label: t('deal.maintenanceMonthly'), value: `$${deal.monthly_maintenance.toLocaleString()}` },
    { label: t('analyze.vacancyRate'), value: `${deal.vacancy_rate}%` },
    { label: t('analyze.propertyManagement'), value: `${deal.property_management_percent}%` },
    { label: t('deal.loanTerm'), value: `${deal.loan_term_years} ${t('deal.years')}` },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
      {items.map(({ label, value }) => (
        <div key={label}>
          <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
          <p className="text-lg font-medium text-slate-900 dark:text-white tabular-nums">{value}</p>
        </div>
      ))}
    </div>
  );
}

function DealCardSection({ deal, dealCardRef, neighborhoodData, aiData, shareNative, generateDealCard, generatingImage, t }) {
  const scores = aiData?.scores || {};
  const decision = scores.recommendation;
  const decisionKey = ['strong_buy', 'buy'].includes(decision) ? 'pass'
    : ['consider', 'review_carefully'].includes(decision) ? 'hold'
    : decision ? 'fail'
    : (deal.deal_score >= 70 ? 'pass' : deal.deal_score >= 40 ? 'hold' : 'fail');
  const decisionLabel = t(`decision.${decisionKey}`).toUpperCase();
  const decisionColor = decisionKey === 'pass' ? '#45C04A' : decisionKey === 'hold' ? '#F59E0B' : '#EF4444';
  const ratingScore = scores.overall ?? deal.deal_score;
  const riskKey = (scores.overall || 0) >= 70 ? 'low' : (scores.overall || 0) >= 40 ? 'moderate' : 'high';
  const riskLabel = t(`decision.${riskKey}Risk`);
  const riskColor = riskKey === 'low' ? 'text-emerald-400' : riskKey === 'moderate' ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-primary font-bold text-xl text-[#12355B] dark:text-white">{t('deal.shareableDealCard')}</h3>
        <div className="flex gap-2">
          <Button data-testid="share-card-btn" onClick={shareNative} size="sm"
            className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl">
            <Share2 className="w-3.5 h-3.5 mr-1.5" /> {t('common.share')}
          </Button>
          <Button data-testid="download-deal-card-button" onClick={generateDealCard} disabled={generatingImage} variant="outline" size="sm" className="rounded-xl">
            <Download className="w-3.5 h-3.5 mr-1.5" /> {generatingImage ? t('deal.generating') : t('common.download')}
          </Button>
        </div>
      </div>

      <div ref={dealCardRef} className="relative overflow-hidden rounded-lg" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)', padding: '28px' }}>
        <div className="absolute top-0 right-0 w-64 h-64 opacity-10" style={{ background: 'radial-gradient(circle, #22c55e 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />

        <div className="relative flex items-start justify-between mb-5">
          <div>
            {/* App RendPleX logo (matches header brand: buildings + green swoosh + "technologies" tagline).
                Uses hardcoded fills — required because this card is captured to PNG via html-to-image,
                which does not reliably resolve CSS variables in embedded SVG. */}
            <div className="flex items-center gap-2 mb-1" data-testid="deal-card-logo">
              <svg viewBox="0 0 200 200" width="28" height="28" fill="none" aria-hidden="true" className="flex-shrink-0">
                {/* Green swoosh arrow */}
                <path d="M 26 116 C 20 152, 62 182, 112 178 C 146 172, 152 128, 170 96 L 178 103 L 190 63 L 150 77 L 158 84 C 146 118, 132 150, 102 158 C 66 166, 38 148, 26 116 Z" fill="#45C04A" />
                {/* Tall navy tower (center-back, 3D) */}
                <polygon points="78,28 118,28 132,20 92,20" fill="#2E77D0" />
                <polygon points="118,28 132,20 132,138 118,146" fill="#12355B" />
                <rect x="78" y="28" width="40" height="118" fill="#1565C0" />
                <rect x="85" y="38" width="9" height="9" fill="white" opacity="0.92" /><rect x="102" y="38" width="9" height="9" fill="white" opacity="0.92" />
                <rect x="85" y="55" width="9" height="9" fill="white" opacity="0.92" /><rect x="102" y="55" width="9" height="9" fill="white" opacity="0.92" />
                <rect x="85" y="72" width="9" height="9" fill="white" opacity="0.92" /><rect x="102" y="72" width="9" height="9" fill="white" opacity="0.92" />
                <rect x="85" y="89" width="9" height="9" fill="white" opacity="0.92" /><rect x="102" y="89" width="9" height="9" fill="white" opacity="0.92" />
                <rect x="85" y="106" width="9" height="9" fill="white" opacity="0.92" /><rect x="102" y="106" width="9" height="9" fill="white" opacity="0.92" />
                {/* Green building (right, 3D) */}
                <polygon points="124,88 152,88 162,82 134,82" fill="#65D95A" />
                <polygon points="152,88 162,82 162,140 152,146" fill="#65D95A" />
                <rect x="124" y="88" width="28" height="58" fill="#45C04A" />
                <rect x="129" y="95" width="7" height="7" fill="white" opacity="0.92" /><rect x="141" y="95" width="7" height="7" fill="white" opacity="0.92" />
                <rect x="129" y="109" width="7" height="7" fill="white" opacity="0.92" /><rect x="141" y="109" width="7" height="7" fill="white" opacity="0.92" />
                <rect x="129" y="123" width="7" height="7" fill="white" opacity="0.92" /><rect x="141" y="123" width="7" height="7" fill="white" opacity="0.92" />
                {/* Blue building (front-left, 3D) */}
                <polygon points="48,72 84,72 94,66 58,66" fill="#3D82D6" />
                <rect x="48" y="72" width="36" height="74" fill="#1565C0" />
                <rect x="53" y="79" width="6" height="7" fill="white" opacity="0.92" /><rect x="63" y="79" width="6" height="7" fill="white" opacity="0.92" /><rect x="73" y="79" width="6" height="7" fill="white" opacity="0.92" />
                <rect x="53" y="94" width="6" height="7" fill="white" opacity="0.92" /><rect x="63" y="94" width="6" height="7" fill="white" opacity="0.92" /><rect x="73" y="94" width="6" height="7" fill="white" opacity="0.92" />
                <rect x="53" y="109" width="6" height="7" fill="white" opacity="0.92" /><rect x="63" y="109" width="6" height="7" fill="white" opacity="0.92" /><rect x="73" y="109" width="6" height="7" fill="white" opacity="0.92" />
                <rect x="53" y="124" width="6" height="7" fill="white" opacity="0.92" /><rect x="63" y="124" width="6" height="7" fill="white" opacity="0.92" /><rect x="73" y="124" width="6" height="7" fill="white" opacity="0.92" />
              </svg>
              <div className="flex flex-col leading-none">
                <span className="font-primary font-semibold text-lg leading-none tracking-tight">
                  <span style={{ color: '#1565C0' }}>RendPle</span><span style={{ color: '#45C04A' }}>X</span>
                </span>
                <span className="font-primary font-medium text-slate-400 lowercase mt-0.5" style={{ fontSize: '8px', letterSpacing: '0.25em' }}>
                  technologies
                </span>
              </div>
            </div>
            <p className="text-slate-400 text-xs">{t('dealCard.subtitle')}</p>
          </div>
          <div className="flex flex-col items-end flex-shrink-0">
            <p className="text-[8px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-1.5 text-right">{t('decision.investmentRating')}</p>
            <div data-testid="deal-card-rating" className="px-4 py-1.5 rounded-lg" style={{ backgroundColor: `${decisionColor}25`, border: `1px solid ${decisionColor}55` }}>
              <span className="text-2xl font-black leading-none" style={{ color: decisionColor }}>{ratingScore}<span className="text-sm font-bold text-slate-400">/100</span></span>
            </div>
          </div>
        </div>

        <div className="relative mb-5 pb-5 border-b border-white/10">
          {deal.property_image_url && (
            <div className="rounded-lg overflow-hidden mb-3 h-36">
              <img src={deal.property_image_url} alt={deal.property_address} className="w-full h-full object-cover" crossOrigin="anonymous"
                onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
              <div className="absolute bottom-2 right-2 bg-black/50 text-white/70 text-[10px] px-2 py-0.5 rounded flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> Street View</div>
            </div>
          )}
          <p className="text-slate-400 text-xs mb-0.5">{t('dealCard.property')}</p>
          <h4 className="font-primary font-bold text-lg text-white mb-1 line-clamp-2">{deal.property_address}</h4>
          <p className="text-2xl font-primary font-black text-white">${deal.purchase_price.toLocaleString()}<span className="text-sm text-slate-400 font-normal ml-1.5">{t('dealCard.askingPrice')}</span></p>
        </div>

        {neighborhoodData && (
          <div className="relative mb-5 pb-5 border-b border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center font-primary font-black text-white ${
                neighborhoodData.location_score >= 75 ? 'bg-emerald-500/30 border border-emerald-500/40' : neighborhoodData.location_score >= 50 ? 'bg-amber-500/30 border border-amber-500/40' : 'bg-red-500/30 border border-red-500/40'
              }`}>
                <span className="text-base leading-none">{neighborhoodData.location_score}</span>
                <span className="text-[7px] text-white/60">LOC</span>
              </div>
              <div><p className="text-white text-xs font-semibold">{t('neighborhood.title')}</p></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'schools', icon: GraduationCap, color: 'text-blue-400' },
                { key: 'transit', icon: Bus, color: 'text-emerald-400' },
                { key: 'shopping', icon: ShoppingBag, color: 'text-violet-400' },
                { key: 'parks', icon: TreePine, color: 'text-green-400' },
                { key: 'restaurants', icon: UtensilsCrossed, color: 'text-orange-400' },
                { key: 'healthcare', icon: Heart, color: 'text-red-400' },
              ].map(({ key, icon: Icon, color }) => (
                <div key={key} className="flex items-center gap-1.5 bg-white/5 rounded px-2 py-1">
                  <Icon className={`w-3 h-3 ${color}`} />
                  <span className="text-white text-xs font-medium">{neighborhoodData[key].count}</span>
                  <span className="text-slate-500 text-[9px] truncate">{t(`neighborhood.${key}`)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="relative grid grid-cols-2 gap-3 mb-5">
          <div className={`rounded-lg p-3 ${deal.monthly_cash_flow >= 0 ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
            <p className="text-slate-400 text-[10px] mb-0.5">{t('dealCard.monthlyCashFlow')}</p>
            <p className={`text-xl font-black ${deal.monthly_cash_flow >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {deal.monthly_cash_flow >= 0 ? '+' : ''}${deal.monthly_cash_flow.toLocaleString()}
            </p>
          </div>
          <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
            <p className="text-slate-400 text-[10px] mb-0.5">{t('dealCard.annualRoi')}</p>
            <p className="text-xl font-black text-blue-400">{deal.roi.toFixed(1)}%</p>
          </div>
        </div>

        {/* Live Preview financial breakdown */}
        <div data-testid="deal-card-financials" className="relative mb-5 pb-5 border-b border-white/10">
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t('analyze.livePreview')}</p>
          <div className="grid grid-cols-4 gap-2">
            {(() => {
              const downPayment = deal.purchase_price * (deal.down_payment_percent / 100);
              const noi = deal.noi ?? ((deal.annual_income || 0) - deal.monthly_expenses * 12);
              const cashOnCash = deal.cash_on_cash ?? (downPayment > 0 ? (deal.annual_cash_flow / downPayment) * 100 : 0);
              return [
                { label: t('analyze.monthlyIncome'), value: `$${Math.round(deal.monthly_income).toLocaleString()}` },
                { label: t('analyze.monthlyMortgage'), value: `-$${Math.round(deal.monthly_mortgage).toLocaleString()}` },
                { label: t('analyze.monthlyExpenses'), value: `-$${Math.round(deal.monthly_expenses).toLocaleString()}` },
                { label: t('analyze.annualCashFlow'), value: `${deal.annual_cash_flow >= 0 ? '+' : '-'}$${Math.abs(Math.round(deal.annual_cash_flow)).toLocaleString()}`, cls: deal.annual_cash_flow >= 0 ? 'text-emerald-400' : 'text-red-400' },
                { label: t('analyze.capRate'), value: `${deal.cap_rate.toFixed(2)}%` },
                { label: t('analyze.noi'), value: `$${Math.round(noi).toLocaleString()}`, cls: noi < 0 ? 'text-red-400' : '' },
                { label: t('analyze.cashOnCash'), value: `${cashOnCash.toFixed(2)}%` },
                { label: t('analyze.downPayment'), value: `$${Math.round(downPayment).toLocaleString()}` },
              ].map(({ label, value, cls }) => (
                <div key={label} className="bg-white/5 rounded-lg px-2 py-1.5 min-w-0">
                  <p className="text-[8px] text-slate-500 uppercase tracking-wide truncate">{label}</p>
                  <p className={`text-xs font-bold tabular-nums ${cls || 'text-white'}`}>{value}</p>
                </div>
              ));
            })()}
          </div>
        </div>

        {/* Risk Analysis - shown when AI data is available */}
        {aiData && decision && (
          <div data-testid="deal-card-risk-analysis" className="relative mb-5 pb-5 border-b border-white/10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${decisionColor}20`, border: `1px solid ${decisionColor}40` }}>
                  <span className="text-xs font-black" style={{ color: decisionColor }}>{decisionLabel}</span>
                </div>
                <div>
                  <p className="text-white text-xs font-semibold">{t('dealCard.aiRiskAnalysis')}</p>
                  <p className={`text-[10px] font-bold ${riskColor}`}>{riskLabel} {t('dealCard.riskSuffix')}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black text-white leading-none">{scores.overall}</p>
                <p className="text-[8px] text-slate-500 font-bold tracking-widest">/100</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-400/70 mb-1">{t('dealCard.strengths')}</p>
                {(aiData.strengths || []).slice(0, 3).map((s, i) => (
                  <div key={i} className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-1 h-1 rounded-full bg-emerald-400 flex-shrink-0" />
                    <span className="text-[10px] text-slate-300 leading-tight">{s.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-red-400/70 mb-1">{t('dealCard.risks')}</p>
                {(aiData.risks || []).slice(0, 3).map((r, i) => (
                  <div key={i} className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-1 h-1 rounded-full bg-red-400 flex-shrink-0" />
                    <span className="text-[10px] text-slate-300 leading-tight">{r.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="relative bg-gradient-to-r from-violet-600/30 to-indigo-600/30 border border-violet-500/30 rounded-lg p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp className="w-4 h-4 text-violet-400" />
            <p className="text-violet-300 text-xs font-medium">{t('dealCard.suggestedOffer')}</p>
          </div>
          <p className="text-2xl font-black text-white">${deal.suggested_offer_price.toLocaleString()}</p>
          {deal.suggested_offer_price < deal.purchase_price && (
            <p className="text-violet-300 text-xs">{t('dealCard.save')} ${(deal.purchase_price - deal.suggested_offer_price).toLocaleString()} ({(((deal.purchase_price - deal.suggested_offer_price) / deal.purchase_price) * 100).toFixed(0)}% {t('dealCard.below')})</p>
          )}
        </div>

        <div className="relative mt-4 pt-3 border-t border-white/10 flex justify-between text-[10px] text-slate-500">
          <span>{t('dealCard.analysis')}: {new Date(deal.created_at).toLocaleDateString()}</span>
          <span>rendplex.ca</span>
        </div>
      </div>
    </div>
  );
}

function ShareModal({ deal, t, getShareUrl, getShareText, copyLink, copied, generateDealCard, generatingImage, onClose }) {
  const socials = [
    { id: 'twitter', label: t('share.twitter'), bg: 'bg-black hover:bg-gray-800', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}&url=${encodeURIComponent(getShareUrl())}`,
      svg: <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/> },
    { id: 'facebook', label: t('share.facebook'), bg: 'bg-[#1877F2] hover:bg-[#166FE5]', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`,
      svg: <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/> },
    { id: 'linkedin', label: t('share.linkedin'), bg: 'bg-[#0A66C2] hover:bg-[#004182]', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl())}`,
      svg: <><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></> },
    { id: 'whatsapp', label: t('share.whatsapp'), bg: 'bg-[#25D366] hover:bg-[#128C7E]', url: `https://wa.me/?text=${encodeURIComponent(getShareText() + ' ' + getShareUrl())}`,
      svg: <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/> },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-[#131824] rounded-xl shadow-2xl max-w-md w-full p-6 relative" onClick={e => e.stopPropagation()}>
        <button data-testid="close-share-modal" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        <div className="text-center mb-5">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-2"><Share2 className="w-6 h-6 text-white" /></div>
          <h3 className="font-bold text-lg text-slate-900 dark:text-white">{t('share.title')}</h3>
          <p className="text-slate-500 text-sm">{t('share.subtitle')}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-5">
          {socials.map(s => (
            <button key={s.id} data-testid={`share-${s.id}`} onClick={() => { window.open(s.url, '_blank', 'noopener,noreferrer,width=600,height=400'); toast.success(t(`share.opening${s.id.charAt(0).toUpperCase() + s.id.slice(1)}`)); }}
              className={`flex items-center justify-center gap-2 p-2.5 rounded-lg ${s.bg} text-white font-medium text-sm transition-colors`}>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">{s.svg}</svg>
              {s.label}
            </button>
          ))}
        </div>
        <div className="border-t border-slate-200 dark:border-slate-700 pt-3 mb-3">
          <Button data-testid="share-download-card-button" onClick={generateDealCard} disabled={generatingImage} variant="outline" className="w-full rounded-lg">
            <Download className="w-3.5 h-3.5 mr-1.5" /> {generatingImage ? t('deal.generating') : t('share.downloadCard')}
          </Button>
          <p className="text-xs text-slate-400 mt-1.5 text-center">{t('share.downloadCardHint')}</p>
        </div>
        <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">{t('share.orCopyLink')}</p>
          <div className="flex gap-2">
            <input type="text" readOnly value={getShareUrl()} className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-slate-300 truncate" />
            <Button data-testid="copy-link-button" onClick={copyLink} variant={copied ? 'default' : 'outline'}
              className={`rounded-lg min-w-[90px] ${copied ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : ''}`}>
              {copied ? <><Check className="w-3.5 h-3.5 mr-1" />{t('common.copied')}</> : <><Copy className="w-3.5 h-3.5 mr-1" />{t('common.copy')}</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
